import { X } from 'lucide-react';
import { type ReactNode, useEffect, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { Icon } from './icon';

// Toasts (S30 «Avvisi brevi»): success with ✓, info with a brand dot, error
// with !; an optional action (Undo, Show, Retry). Bottom-right on desktop,
// above the tab bar on the phone.

export type ToastKind = 'success' | 'info' | 'error';
export interface ToastInput {
  kind?: ToastKind;
  text: ReactNode;
  action?: { label: ReactNode; onClick(): void };
  /** ms; errors stay until closed. */
  duration?: number;
}
interface ToastItem extends ToastInput {
  id: number;
}

let items: ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const emit = () => {
  for (const l of listeners) l();
};

export function toast(input: ToastInput): number {
  const id = nextId++;
  items = [...items, { ...input, id }].slice(-4);
  emit();
  return id;
}
export function dismissToast(id: number): void {
  items = items.filter((x) => x.id !== id);
  emit();
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

export function ToastView({ item, onClose }: { item: ToastInput; onClose?: () => void }) {
  const { t } = useTranslation();
  const kind = item.kind ?? 'info';
  return (
    <div className="toast" role={kind === 'error' ? 'alert' : 'status'}>
      {kind === 'success' ? <span className="rg c-ok b-ok">✓</span> : null}
      {kind === 'info' ? <span className="dot d-br" /> : null}
      {kind === 'error' ? <span className="rg c-bl b-bl">!</span> : null}
      <span>{item.text}</span>
      {item.action ? (
        <Button size="sm" style={{ marginLeft: 'auto' }} onClick={item.action.onClick}>
          {item.action.label}
        </Button>
      ) : null}
      {onClose ? (
        <Button
          size="sm"
          variant="ghost"
          icon
          aria-label={t('actions.close')}
          onClick={onClose}
          style={item.action ? undefined : { marginLeft: 'auto' }}
        >
          <Icon as={X} size="s" />
        </Button>
      ) : null}
    </div>
  );
}

function AutoClose({ item }: { item: ToastItem }) {
  useEffect(() => {
    const ms = item.duration ?? (item.kind === 'error' ? 0 : 6000);
    if (!ms) return;
    const timer = setTimeout(() => dismissToast(item.id), ms);
    return () => clearTimeout(timer);
  }, [item]);
  return <ToastView item={item} onClose={() => dismissToast(item.id)} />;
}

export function Toaster() {
  const list = useSyncExternalStore(subscribe, () => items);
  if (list.length === 0) return null;
  return (
    <div
      className="fixed right-6 bottom-6 flex flex-col gap-2 max-md:right-3 max-md:bottom-[84px] max-md:left-3"
      style={{ zIndex: 'var(--z-toast)' }}
      aria-live="polite"
    >
      {list.map((item) => (
        <AutoClose key={item.id} item={item} />
      ))}
    </div>
  );
}
